import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, OrderStatus, Price, Spinner } from "../components/ui.tsx";
import { useIdentities, useMe } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopSellerOrders } from "./desktop/seller-orders.tsx";

/**
 * 我卖出去的单 —— **商家的售后入口**。
 *
 * 🔴 之前商家根本看不到自己卖出去的订单（只有供应商能看发货单），
 *    客户问"我的货到哪了"他答不上来，也不知道自己到底卖了什么。
 *
 * ⚠️ 服务端按 `sellerOrgId` 过滤，他看不到别人店里的单；
 *    出参里也没有上游成本 —— 那在价格链里，要裁剪后才给。
 */

export interface SellerOrder {
  id: number;
  orderNo: string;
  payment: string;
  fulfillment: string;
  goodsAmount?: number;
  payableAmount?: number;
  /**
   * 「经过我这一级」视角下，这单**我收到多少**（我的下级付给我的）。
   *
   * 🔴 不给终端价 —— 那是更下游的售价，给了上级就能算出下游赚多少。
   */
  myAmount?: number;
  /**
   * 这一单我该**付给上级多少**（我的进货成本）。
   *
   * 🔴 线下收款模式下钱不经平台：我收下级的钱、再自己把货款结给上级 ——
   *    不摆出这个数，商家只能自己按计算器，按错就是真金白银的差错。
   * ⚠️ 这不是泄露：进货价是上级报给他的，他在「我的定价」里天天看得到。
   */
  myCost?: number;
  /** 是不是我自己直接卖的（false = 下游卖的，货从我这里出）。 */
  isMine?: boolean;
  shipName: string;
  shipPhone: string;
  /**
   * 物流。
   * 🔴 **全链同步** —— 一笔交易在每一级都有一张联动的订单，
   *    商品信息和物流单号是同一份，只有金额各看各的。
   *    上游要知道货到哪了（客户问到他这儿时答得上来）。
   */
  /** ⚠️ 库里的列叫 `shipping_carrier` —— 写成 `carrier` 会静默拿到 undefined，
   *     页面上只剩一串没有承运商的单号，用户不知道该去哪家查件。 */
  shippingCarrier?: string | null;
  trackingNo?: string | null;
  createdAt: string;
  /**
   * ⚠️ 字段名是 `*Snapshot` —— 订单明细存的是**下单当时的快照**，
   *    商家改价改名、SKU 下架都不影响历史订单的展示。
   */
  items: {
    id: number;
    titleSnapshot: string;
    specsSnapshot: Record<string, string> | null;
    quantity: number;
    unitPrice?: number;
    subtotal?: number;
  }[];
}

export const TABS = [
  { key: "", label: "全部" },
  { key: "pending", label: "待付款" },
  { key: "paid", label: "已付款" },
] as const;

const orgGet = <T,>(path: string, orgId: number) => orgFetch<T>(path, orgId);

/** 手机号中间四位打码 —— 商家要联系客户，但列表里没必要全露出来。 */
export function maskPhone(phone: string): string {
  return phone.length === 11 ? `${phone.slice(0, 3)}****${phone.slice(-4)}` : phone;
}

/**
 * 履约与售后策略。
 *
 * 🔴 这两个开关以前只是"记录了一个约定"，代码从来没读过 ——
 *    老板改成「卖家自己备货发货」，卖家这边还是看不到任何要发的货。
 *    现在前端也要跟着走：该他发货时才出现发货入口。
 */
export interface FulfillmentPolicy {
  shipBy: "supplier_direct" | "seller_stock";
  afterSaleBy: "seller" | "platform";
}

export interface PendingRefund {
  id: number;
  refundNo: string;
  orderNo: string;
  paidAmount: number;
  alreadyRefunded: number;
  amount: number;
  reason: string;
  createdAt: string;
}

const orgPost = <T,>(path: string, orgId: number, body: unknown) =>
  orgFetch<T>(path, orgId, { method: "POST", body: JSON.stringify(body) });

/**
 * 确认收到线下货款。
 *
 * 🔴 **这个商城不走线上支付**：线上只建订单，钱走别的渠道
 *    （转账 / 微信 / 现金 / 对公）。收款方在这里点一下，货才发得出去。
 *
 * ⚠️ **收款方式必须留痕**：线下收款没有第三方流水做旁证，
 *    这条记录就是唯一的凭据，纠纷时全靠它。
 */
export const PAY_METHODS = ["微信", "支付宝", "银行转账", "现金", "对公转账"] as const;

function ConfirmPayBox({
  orderId,
  orgId,
  amount,
  onDone,
}: {
  orderId: number;
  orgId: number;
  amount: number;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<string>(PAY_METHODS[0]);
  const [note, setNote] = useState("");

  const confirm = useMutation({
    mutationFn: () => orgPost(`/orders/${orderId}/confirm-payment`, orgId, { method, note }),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  if (!open) {
    return (
      <div className="border-t border-neutral-100 px-4 py-2.5">
        <Button size="sm" className="w-full" onClick={() => setOpen(true)}>
          确认已收款 <Price cents={amount} />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t border-neutral-100 px-4 py-2.5">
      {/*
        🔴 金额是**服务端算的**，这里只显示不让改 ——
           让前端传金额等于点一下就能把「已收 ¥5824」记成「已收 ¥1」。
      */}
      <p className="text-[13px] text-ink-500">
        确认收到 <Price cents={amount} className="text-brand-600" /> ？确认后这单就可以发货了。
      </p>
      <div className="flex flex-wrap gap-1.5">
        {PAY_METHODS.map((m) => (
          <button
            key={m}
            onClick={() => setMethod(m)}
            className={
              "rounded-full border px-3 py-1.5 text-[13px] " +
              (method === m
                ? "border-brand-600 bg-brand-50 text-brand-700"
                : "border-neutral-200 text-ink-500")
            }
          >
            {m}
          </button>
        ))}
      </div>
      <Input
        placeholder="备注（流水号、经手人，选填）"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={() => setOpen(false)}>
          取消
        </Button>
        <Button
          size="sm"
          className="flex-1"
          loading={confirm.isPending}
          onClick={() => confirm.mutate()}
        >
          确认收款
        </Button>
      </div>
      {confirm.error && <ErrorBlock error={confirm.error} />}
    </div>
  );
}

/** 发货表单。⚠️ 承运商和单号都必填 —— 少一个买家就查不到物流。 */
function ShipBox({ orderId, orgId, onDone }: { orderId: number; orgId: number; onDone: () => void }) {
  const [carrier, setCarrier] = useState("");
  const [trackingNo, setTrackingNo] = useState("");

  const ship = useMutation({
    mutationFn: () => orgPost(`/supply-orders/${orderId}/ship`, orgId, { carrier, trackingNo }),
    onSuccess: onDone,
  });

  return (
    <div className="border-t border-neutral-100 px-4 py-2.5">
      <div className="flex gap-2">
        <Input placeholder="承运商" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        <Input
          placeholder="快递单号"
          value={trackingNo}
          onChange={(e) => setTrackingNo(e.target.value)}
        />
        <Button
          size="sm"
          loading={ship.isPending}
          disabled={!carrier.trim() || !trackingNo.trim()}
          onClick={() => ship.mutate()}
        >
          发货
        </Button>
      </div>
      {ship.error && <ErrorBlock error={ship.error} />}
    </div>
  );
}

/**
 * 我店里的待审退款。
 *
 * 🔴 `afterSaleBy=seller` 时售后归卖家处理，但以前**只有平台后台有入口** ——
 *    这个开关等于摆设，卖家收到客户的退款申请也没地方处理。
 * ⚠️ 服务端只出他自己店里的单：别人的退款申请里有订单号、金额和买家诉求。
 */
function SellerRefunds({ orgId }: { orgId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["seller-refunds", orgId],
    queryFn: () =>
      orgGet<{ items: PendingRefund[]; policy: FulfillmentPolicy }>("/refunds", orgId),
  });

  const done = () => {
    qc.invalidateQueries({ queryKey: ["seller-refunds", orgId] });
    qc.invalidateQueries({ queryKey: ["seller-orders"] });
  };
  const approve = useMutation({
    mutationFn: (id: number) => orgPost(`/refunds/${id}/approve`, orgId, {}),
    onSuccess: done,
  });
  const reject = useMutation({
    mutationFn: (v: { id: number; reason: string }) =>
      orgPost(`/refunds/${v.id}/reject`, orgId, { reason: v.reason }),
    onSuccess: done,
  });

  /* ⚠️ 平台统一处理售后时这一块整个不出现，别给一个点不动的入口 */
  if (q.data?.policy.afterSaleBy === "platform") {
    return (
      <div className="mt-2 bg-white px-4 py-3 text-xs text-ink-500">
        当前<strong>售后由平台统一处理</strong>，客户的退款申请会直接进平台客服队列。
      </div>
    );
  }
  if (q.isPending || (q.data?.items.length ?? 0) === 0) return null;

  return (
    <div className="mt-2 bg-white">
      <p className="px-4 pt-3 text-sm font-medium">
        待处理退款（{q.data!.items.length}）
      </p>
      {/* 🔴 批了钱就从他的分账里扣回来，这句话要说在按钮旁边 */}
      <p className="px-4 pt-1 text-xs text-ink-500">
        同意之后钱立刻退给买家，<strong>并从你这一单的分成里扣回</strong>。
      </p>
      {q.data!.items.map((r) => (
        <div key={r.id} className="border-t border-neutral-100 px-4 py-3">
          <p className="font-mono text-xs text-ink-500">{r.orderNo}</p>
          <p className="mt-1 text-sm">
            申请退 <Price cents={r.amount} className="text-red-600" />
            <span className="ml-1.5 text-xs text-ink-500">
              实付 <Price cents={r.paidAmount} />
              {r.alreadyRefunded > 0 && (
                <span className="ml-1">· 已退 <Price cents={r.alreadyRefunded} /></span>
              )}
            </span>
          </p>
          <p className="mt-0.5 text-xs text-ink-500">理由：{r.reason || "（未填）"}</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate(r.id)}>
              同意退款
            </Button>
            <Button
              size="sm"
              variant="outline"
              loading={reject.isPending}
              onClick={() => {
                /* ⚠️ 驳回必须写理由 —— 买家要知道为什么被拒，否则只会反复申请 */
                const reason = window.prompt("驳回理由（会展示给买家）");
                if (reason && reason.trim()) reject.mutate({ id: r.id, reason: reason.trim() });
              }}
            >
              驳回
            </Button>
          </div>
          {(approve.error || reject.error) && (
            <ErrorBlock error={approve.error ?? reject.error!} />
          )}
        </div>
      ))}
    </div>
  );
}

function SellerOrders() {
  const search = useSearch({ from: "/seller-orders" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  const orgs = (identities?.orgs ?? []).filter((o) => o.status === "active");
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;

  const [payment, setPayment] = useState("");
  const [keyword, setKeyword] = useState("");

  /** 该不该出现发货入口，由服务端的 `shipBy` 说了算。 */
  const policy = useQuery({
    queryKey: ["fulfillment-policy", orgId],
    queryFn: () => orgGet<FulfillmentPolicy>("/fulfillment-policy", orgId!),
    enabled: Boolean(orgId),
  });

  /**
   * 看哪一批单。
   *
   * 🔴 **「从我这出货的」这一档是给上级看的。**
   *    王师傅卖出一件瓷砖，货是从经销商那里进的 —— 经销商得知道
   *    "我这里出了一件货、要给王师傅备货"。只看"我卖的"的话，
   *    中间层级对下游的成交完全不可见，备货和对账都无从谈起。
   */
  const [scope, setScope] = useState<"mine" | "chain">("mine");

  const list = useQuery({
    queryKey: ["seller-orders", orgId, payment, keyword, scope],
    queryFn: () => {
      const qs = new URLSearchParams({ limit: "30" });
      if (payment) qs.set("payment", payment);
      if (keyword) qs.set("keyword", keyword);
      if (scope === "chain") qs.set("scope", "chain");
      return orgGet<{ items: SellerOrder[]; nextCursor: string | null }>(
        `/orders?${qs}`,
        orgId!,
      );
    },
    enabled: Boolean(orgId),
  });

  if (mePending) {
    return (
      <SubPage title="我的订单">
        <Spinner />
      </SubPage>
    );
  }

  if (!me || orgs.length === 0) {
    return (
      <SubPage title="我的订单">
        <Empty title="你还不是任何商家的成员" hint="这是商家功能，联系上级商家或平台开通" />
      </SubPage>
    );
  }

  return (
    <SubPage title="我的订单">
      {orgs.length > 1 && (
        <div className="flex gap-2 overflow-x-auto bg-white px-4 py-2.5">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
                o.id === orgId ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
              }`}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      <div className="sticky top-0 z-10 bg-white">
        {/* 我卖的 / 从我这出货的 */}
        <div className="flex gap-0.5 border-b border-border px-4 pt-1">
          {(
            [
              { k: "mine", label: "我卖的" },
              { k: "chain", label: "从我这出货的" },
            ] as const
          ).map((t) => (
            <button
              key={t.k}
              onClick={() => setScope(t.k)}
              className={`relative px-3 py-2.5 text-sm ${
                scope === t.k
                  ? "font-medium text-brand-600 after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-brand-600"
                  : "text-ink-500"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {scope === "chain" && (
          <p className="bg-brand-50 px-4 py-2 text-[12px] leading-relaxed text-brand-700">
            这里是<strong>经过你这一级</strong>的全部订单，含你下级卖出去的。
            <strong>收</strong>是你这一单收到多少（下级付给你的），
            <strong>该付上级</strong>是你要结给上家的货款 —— 钱不经平台，
            两边都是你自己线下收付。
            商品和物流全链同步；下游最终卖了多少钱是他的经营信息，这里不显示。
          </p>
        )}

        <div className="flex gap-2 px-4 py-2.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setPayment(t.key)}
              className={`rounded-full px-3 py-1.5 text-sm ${
                payment === t.key ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="border-t border-neutral-100 px-4 py-2.5">
          <Input
            placeholder="搜订单号或客户手机号"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
      </div>

      {orgId && <SellerRefunds orgId={orgId} />}

      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBlock error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <Empty title="还没有订单" hint="把专属链接发给客户，他们下的单会出现在这里" />
      ) : (
        <div className="mt-2 space-y-2">
          {list.data.items.map((o) => (
            <section key={o.id} className="bg-white">
              <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-2.5">
                <span className="font-mono text-xs text-ink-500">{o.orderNo}</span>
                <OrderStatus payment={o.payment as never} fulfillment={o.fulfillment as never} />
              </div>

              {o.items.map((it) => (
                <div key={it.id} className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="min-w-0 flex-1 pr-3">
                    <span className="line-clamp-1">{it.titleSnapshot}</span>
                    {/* ⚠️ 快照可能是空对象甚至 null（老数据），别直接 Object.values */}
                    <span className="text-xs text-ink-500">
                      {Object.values(it.specsSnapshot ?? {}).join(" · ")}
                    </span>
                  </span>
                  <span className="shrink-0 text-right text-ink-500">
                    {/* ⚠️ 链视角下服务端不给单价 —— 那是下游的售价 */}
                    {it.unitPrice != null ? <Price cents={it.unitPrice} /> : <span>—</span>}
                    <span className="block text-xs">×{it.quantity}</span>
                  </span>
                </div>
              ))}

              <div className="flex items-center justify-between border-t border-neutral-100 px-4 py-2.5 text-sm">
                {/* 商家要能联系上客户处理售后，但手机号中间打码 */}
                <span className="text-xs text-ink-500">
                  {o.shipName} {maskPhone(o.shipPhone)}
                </span>
                {/*
                  ⚠️ 两个视角显示的是**不同的数**：
                     「我卖的」显示终端成交价；「从我这出货的」显示按我这一级算的钱。
                     标签也要跟着变 —— 都叫"成交"的话，上级会以为那是终端价。
                */}
                <span>
                  {o.payableAmount != null ? (
                    <>
                      成交 <Price cents={o.payableAmount} className="text-brand-600" />
                    </>
                  ) : (
                    <>
                      本单收款 <Price cents={o.myAmount ?? 0} className="text-brand-600" />
                      {o.isMine === false && (
                        <span className="ml-1.5 text-[11px] text-ink-500">下级卖的</span>
                      )}
                    </>
                  )}
                </span>
              </div>

              {/*
                🔴 **对账**：钱不经平台，我收下级的钱、再自己把货款结给上级。
                   不摆出「该付上级多少」，商家只能自己按计算器 ——
                   按错就是真金白银的差错。
              */}
              {o.myCost != null && o.myCost > 0 && (
                <div className="flex items-center justify-between border-t border-neutral-100 px-4 py-2 text-xs">
                  <span className="text-ink-500">该付上级</span>
                  <Price cents={o.myCost} />
                </div>
              )}

              {/*
                物流。🔴 全链同步 —— 上游也要看得到，
                   客户问到他那儿时才答得上来「货到哪了」。
              */}
              {o.trackingNo && (
                <div className="flex items-center justify-between border-t border-neutral-100 px-4 py-2 text-xs">
                  <span className="text-ink-500">物流</span>
                  <span className="font-mono">
                    {o.shippingCarrier} {o.trackingNo}
                  </span>
                </div>
              )}

              {/*
                🔴 只在「卖家自己备货发货」且这单确实待发时才出发货框。
                   供应商直发时这里出现输入框，卖家填了也发不出去（服务端会拒），
                   那是最糟的交互：看得见、点得动、就是不成。
              */}
              {/*
                🔴 线下收款：**只有这一单的收款方**看得到这个按钮，
                   而且服务端还会再校验一次（路由放行、归谁由 service 判）。
                ⚠️ 只在「我自己卖的 + 还没收款」时出 —— 下级卖的单不该我来确认，
                   他的客户把钱给的是他。
              */}
              {/* ⚠️ 已取消的单不出这个按钮 —— 取消只改 fulfillment，payment 仍然是 pending。
                     服务端也拦了（那才是最后一道防线），这里是别让他点了才知道。 */}
              {o.isMine !== false &&
                o.payment === "pending" &&
                o.fulfillment !== "cancelled" &&
                orgId && (
                <ConfirmPayBox
                  orderId={o.id}
                  orgId={orgId}
                  amount={o.payableAmount ?? o.myAmount ?? 0}
                  onDone={() => list.refetch()}
                />
              )}

              {policy.data?.shipBy === "seller_stock" &&
                o.payment === "paid" &&
                o.fulfillment === "pending" &&
                orgId && (
                  <ShipBox
                    orderId={o.id}
                    orgId={orgId}
                    onDone={() => list.refetch()}
                  />
                )}
            </section>
          ))}
        </div>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function SellerOrdersRoute() {
  return useIsDesktop() ? <DesktopSellerOrders /> : <SellerOrders />;
}

export const sellerOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/seller-orders",
  component: SellerOrdersRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
